import type { NormalizedRoundSchedule, RoundGroup } from "./types";

export type BoardSession = {
  id: "morning" | "afternoon" | "weekend";
  title: "Morning Session" | "Afternoon Session" | "Weekend Session";
  groups: RoundGroup[];
};

function teeOrder(group: RoundGroup) {
  if (group.tee === 1) return 0;
  if (group.tee === 10) return 1;
  return 2;
}

export function orderRoundGroups(groups: RoundGroup[]) {
  return [...groups].sort(
    (a, b) => a.startMinutes - b.startMinutes || a.number - b.number || teeOrder(a) - teeOrder(b),
  );
}

/**
 * Operator-facing session split kept intentionally compatible with develop-v1.
 * Weekdays default to an even half split. A real wave gap wins only when the
 * largest gap between adjacent tee times is at least 45 minutes.
 * R3/R4 are one continuous weekend session.
 */
export function splitBoardSessions(schedule: NormalizedRoundSchedule): BoardSession[] {
  const ordered = orderRoundGroups(schedule.groups);

  if (schedule.round === "R3" || schedule.round === "R4") {
    return [{ id: "weekend", title: "Weekend Session", groups: ordered }];
  }

  if (!ordered.length) {
    return [
      { id: "morning", title: "Morning Session", groups: [] },
      { id: "afternoon", title: "Afternoon Session", groups: [] },
    ];
  }

  let cut = Math.ceil(ordered.length / 2);
  let largestGap = -1;
  let largestGapCut = cut;

  for (let index = 0; index < ordered.length - 1; index += 1) {
    const gap = ordered[index + 1].startMinutes - ordered[index].startMinutes;
    if (gap > largestGap) {
      largestGap = gap;
      largestGapCut = index + 1;
    }
  }

  if (largestGap >= 45) cut = largestGapCut;

  return [
    { id: "morning", title: "Morning Session", groups: ordered.slice(0, cut) },
    { id: "afternoon", title: "Afternoon Session", groups: ordered.slice(cut) },
  ];
}

export function groupSessionColumns(session: BoardSession) {
  const tee1 = session.groups.filter((group) => group.tee === 1);
  const tee10 = session.groups.filter((group) => group.tee === 10);
  const other = session.groups.filter((group) => group.tee !== 1 && group.tee !== 10);
  return [
    ...(tee1.length ? [{ id: "tee-1", title: "Tee 1", groups: tee1 }] : []),
    ...(tee10.length ? [{ id: "tee-10", title: "Tee 10", groups: tee10 }] : []),
    ...(other.length ? [{ id: "tee-other", title: "Tee —", groups: other }] : []),
  ];
}
